import React, { useState, useEffect, useCallback } from 'react';
import { usePendingConnectionConfirmations } from '@/hooks/usePendingConnectionConfirmations';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Video, CalendarCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const STORAGE_KEY = 'dismissed_confirmations';
const TEN_MINUTES = 10 * 60 * 1000;

// LocalStorage helpers
const getDismissedItems = (): Map<string, number> => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? new Map(JSON.parse(stored)) : new Map();
  } catch {
    return new Map();
  }
};

const saveDismissedTimestamp = (id: string, timestamp: number) => {
  const items = getDismissedItems();
  items.set(id, timestamp);
  localStorage.setItem(STORAGE_KEY, JSON.stringify([...items]));
};

const clearDismissedItem = (id: string) => {
  const items = getDismissedItems();
  items.delete(id);
  localStorage.setItem(STORAGE_KEY, JSON.stringify([...items]));
};

interface ConnectionConfirmationModalProps {
  suppressWhenDailyActive?: boolean;
}

export function ConnectionConfirmationModal({ suppressWhenDailyActive = false }: ConnectionConfirmationModalProps) {
  const { t } = useTranslation();
  const { pendingConfirmations, confirmConnection } = usePendingConnectionConfirmations();
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState<string | null>(null);

  // Get the first non-dismissed confirmation to show
  const currentConfirmation = pendingConfirmations.find(
    (c) => !dismissedIds.has(c.id)
  );

  // Handle dismiss (clicking outside or ESC)
  const handleDismiss = useCallback((confirmationId: string) => {
    saveDismissedTimestamp(confirmationId, Date.now());
    setDismissedIds((prev) => new Set([...prev, confirmationId]));
  }, []);

  // Handle confirm (Yes or No)
  const handleConfirm = async (confirmed: boolean) => {
    if (!currentConfirmation) return;

    setLoading(currentConfirmation.id);
    
    const success = await confirmConnection(
      currentConfirmation.id,
      confirmed,
      undefined,
      currentConfirmation.source
    );

    if (success) {
      toast.success(
        confirmed
          ? t('connectionConfirmation.confirmed', 'Conexão confirmada com sucesso!')
          : t('connectionConfirmation.notConfirmed', 'Conexão não realizada registrada.')
      );
      // Clear from dismissed list since it's now answered
      clearDismissedItem(currentConfirmation.id);
      setDismissedIds((prev) => {
        const next = new Set(prev);
        next.delete(currentConfirmation.id);
        return next;
      });
    } else {
      toast.error(t('connectionConfirmation.error', 'Erro ao registrar confirmação.'));
    }

    setLoading(null);
  };

  // Timer to check and reappear after 10 minutes
  useEffect(() => {
    const checkReappear = () => {
      const dismissed = getDismissedItems();
      const now = Date.now();
      let hasChanges = false;

      pendingConfirmations.forEach((confirmation) => {
        const dismissedAt = dismissed.get(confirmation.id);
        if (dismissedAt && now - dismissedAt >= TEN_MINUTES) {
          // Clear from localStorage and state - time to reappear
          clearDismissedItem(confirmation.id);
          hasChanges = true;
        }
      });

      if (hasChanges) {
        // Refresh dismissed IDs from localStorage
        const currentDismissed = getDismissedItems();
        setDismissedIds(new Set(currentDismissed.keys()));
      }
    };

    // Check immediately on mount and when pendingConfirmations change
    checkReappear();

    // Check every 30 seconds
    const interval = setInterval(checkReappear, 30000);
    return () => clearInterval(interval);
  }, [pendingConfirmations]);

  // Sync dismissed state with localStorage on mount
  useEffect(() => {
    const dismissed = getDismissedItems();
    setDismissedIds(new Set(dismissed.keys()));
  }, []);

  // Clean up old entries from localStorage that are no longer pending
  useEffect(() => {
    const dismissed = getDismissedItems();
    const pendingIds = new Set(pendingConfirmations.map((c) => c.id));
    
    dismissed.forEach((_, id) => {
      if (!pendingIds.has(id)) {
        clearDismissedItem(id);
      }
    });
  }, [pendingConfirmations]);

  if (!currentConfirmation) {
    return null;
  }

  const isScheduled = currentConfirmation.source === 'scheduled';
  const formattedTime = formatDistanceToNow(
    new Date(currentConfirmation.acceptedAt),
    { addSuffix: true, locale: ptBR }
  );

  return (
    <Dialog open={!!currentConfirmation && !suppressWhenDailyActive} modal={false}>
      <DialogContent
        className="max-w-md z-[100]"
        onEscapeKeyDown={() => handleDismiss(currentConfirmation.id)}
        onInteractOutside={() => handleDismiss(currentConfirmation.id)}
      >
        <DialogHeader>
          {/* Large icon */}
          <div
            className={`flex items-center justify-center w-20 h-20 mx-auto mb-4 rounded-full ${
              isScheduled
                ? 'bg-blue-100 dark:bg-blue-900/30'
                : 'bg-primary/10'
            }`}
          >
            {isScheduled ? (
              <CalendarCheck className="w-10 h-10 text-blue-600 dark:text-blue-400" />
            ) : (
              <Video className="w-10 h-10 text-primary" />
            )}
          </div>

          <DialogTitle className="text-center text-xl font-bold">
            {t('connectionConfirmation.title', 'Confirma que a reunião aconteceu?')}
          </DialogTitle>

          <DialogDescription className="text-center text-lg mt-2">
            {t('connectionConfirmation.meetingWith', 'Reunião com')}{' '}
            <strong className="text-foreground">
              {currentConfirmation.contactName || 'Cliente'}
            </strong>
            {currentConfirmation.scheduledTitle && isScheduled && (
              <>
                <br />
                <span className="text-sm italic">"{currentConfirmation.scheduledTitle}"</span>
              </>
            )}
            <br />
            <span className="text-sm text-muted-foreground">
              {isScheduled
                ? t('connectionConfirmation.scheduledBy', 'Agendada por')
                : t('connectionConfirmation.initiatedBy', 'Iniciada por')}{' '}
              {currentConfirmation.requestedByName || 'SDR'} • {formattedTime}
            </span>
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="flex gap-4 sm:justify-center mt-6">
          <Button
            size="lg"
            className="flex-1 h-12 text-lg font-semibold"
            onClick={() => handleConfirm(true)}
            disabled={loading === currentConfirmation.id}
          >
            ✅ {t('connectionConfirmation.yes', 'Sim!')}
          </Button>
          <Button
            size="lg"
            variant="outline"
            className="flex-1 h-12 text-lg font-semibold"
            onClick={() => handleConfirm(false)}
            disabled={loading === currentConfirmation.id}
          >
            ❌ {t('connectionConfirmation.no', 'Não')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
